import { useState } from "react";
import { formatTime } from "../lib/format";
import { Icon } from "./icons";
import { backdropClose } from "../lib/backdrop";

/**
 * Removing audio, and saying what goes with it.
 *
 * This is the one action in the hub with nothing behind it. A file does not
 * leave alone: its proofing, its pickups and every earlier version of it go at
 * the same time, and none of that is anywhere else. Until now a single file
 * went on ONE CLICK of an unlabelled ×, with no warning at all - which was
 * survivable while it was one file at a time and is not once the button can
 * take thirty-one.
 *
 * So the dialog's job is not to ask "are you sure" - nobody reads that - but to
 * say WHAT IS ABOUT TO BE LOST, in numbers. Where proofed work is among it, the
 * confirmation has to be typed: the cost of getting that wrong is days.
 */

export interface Doomed {
  name: string;
  runtime: number;
  proofed: boolean;
  pickups: number;
  olderVersions: number;
}

export function DeleteFilesDialog({ files, busy, onCancel, onConfirm }: {
  files: Doomed[];
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const runtime = files.reduce((n, f) => n + f.runtime, 0);
  const proofed = files.filter((f) => f.proofed).length;
  const pickups = files.reduce((n, f) => n + f.pickups, 0);
  const versions = files.reduce((n, f) => n + f.olderVersions, 0);

  // Typed only where real work would go. Asking for it on an unproofed drop
  // would train the habit of typing it without reading.
  const needsTyping = proofed > 0 || pickups > 0;
  const ready = !busy && (!needsTyping || typed.trim().toLowerCase() === "delete");

  return (
    <div className="modal-overlay" {...backdropClose(() => { if (!busy) onCancel(); })}>
      <div className="modal-card dfd" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3>
          {files.length === 1 ? "Delete this file?" : `Delete ${files.length} files?`}
        </h3>

        <div className="dfd-cost">
          <Cost n={files.length} what={files.length === 1 ? "file" : "files"} />
          {runtime > 0 && <Cost n={formatTime(runtime)} what="of audio" />}
          {proofed > 0 && <Cost n={proofed} what={proofed === 1 ? "proofed chapter" : "proofed chapters"} warn />}
          {pickups > 0 && <Cost n={pickups} what={pickups === 1 ? "pickup" : "pickups"} warn />}
          {versions > 0 && <Cost n={versions} what={versions === 1 ? "earlier version" : "earlier versions"} />}
        </div>

        <div className="dfd-list">
          {files.map((f) => (
            <div key={f.name} className="dfd-row">
              <span className="dfd-name mono">{f.name}</span>
              <span className="dfd-note">
                {f.runtime ? formatTime(f.runtime) : ""}
                {f.proofed && <em className="warn"> · proofed</em>}
                {f.pickups > 0 && <em className="warn"> · {f.pickups} pickup{f.pickups === 1 ? "" : "s"}</em>}
                {f.olderVersions > 0 && <em> · +{f.olderVersions} earlier</em>}
              </span>
            </div>
          ))}
        </div>

        <p className="dfd-warn">
          <Icon name="alert" size={14} />
          {proofed > 0 || pickups > 0
            ? " The proofing and pickups on these files go too. There is no undo, and nothing keeps a copy."
            : " There is no undo, and nothing keeps a copy."}
        </p>

        {needsTyping && (
          <label className="dfd-type">
            <span>Type <b>delete</b> to confirm</span>
            <input className="input" value={typed} autoFocus disabled={busy}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && ready) onConfirm(); }} />
          </label>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn danger" disabled={!ready} onClick={onConfirm}>
            {busy ? "Deleting…" : files.length === 1 ? "Delete file" : `Delete ${files.length} files`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Cost({ n, what, warn }: { n: number | string; what: string; warn?: boolean }) {
  return (
    <span className={`dfd-cost-item${warn ? " warn" : ""}`}>
      <b>{n}</b> {what}
    </span>
  );
}
